<template>
  <section class="purchase-successful">
    <BittsButton
      :center-icon="['fak', 'x']"
      aria-label="Close"
      variant="ghost"
      type="white"
      size="large"
      class="close-icon"
      data-testid="close-icon"
      @click="handleClose(true)"
    />
    <div class="welcome-to-the-team__content">
      <BittsSvg svg="logoNoText" class="welcome-to-the-team__logo" />
      <h1 class="main-title">
        {{ V5_BILLING_COPY.purchaseSuccessful.title }}
      </h1>
      <BittsUserStack
        data-testid="large-user-stack"
        :users
        size="large"
        class="mb-24"
      />
      <p class="description">
        {{
          !isOnboarded
            ? V5_BILLING_COPY.purchaseSuccessful.descriptionOnboarding
            : V5_BILLING_COPY.purchaseSuccessful.description
        }}
      </p>
      <div v-if="isOnboarded" class="button-grid">
        <BittsButton
          v-for="link in routerLinks"
          :key="link.name"
          v-bind="getProps(link)"
          v-on="getEventHandlers(link)"
        >
          <FontAwesomeIcon
            class="mb-2 text-info-accent"
            :icon="['fak', link.icon]"
            :style="{ height: '16px', width: '16px' }"
          />
          {{ link.title }}
        </BittsButton>
      </div>
      <BittsButton
        size="large"
        v-bind="continueButtonProps"
        @click="handleClose"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsButtonProps,
  BittsSvg,
  BittsUserStack,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { onBeforeRouteLeave, useRouter } from 'vue-router';

import useAuth from '@/composables/useAuth';
import useIteratively from '@/composables/useIteratively';
import { V5_BILLING_COPY } from '@/constants/billing';
import { BASE_ROUTES } from '@/constants/routes';
import { useTeamStore } from '@/stores';

useHead({ title: 'Welcome' });

const router = useRouter();
const teamStore = useTeamStore();

const { coreUsers, salesOnlyUsers } = storeToRefs(teamStore);
const users = computed(() =>
  [...coreUsers.value, ...salesOnlyUsers.value].map((auth) => auth.user),
);

const { currentOrg } = useAuth();
const isOnboarded = computed(() => currentOrg.value.setup_menu_hidden);

const continueButtonProps = computed<BittsButtonProps>(() => {
  return !isOnboarded.value
    ? {
        text: 'Finish onboarding',
        type: 'neutral',
        class: 'mt-16',
      }
    : {
        text: 'Continue to Crossbeam',
        type: 'white',
        variant: 'ghost',
        class: 'mt-40',
      };
});

const routerLinks = [
  {
    name: BASE_ROUTES.REPORTS,
    icon: 'mapping',
    dataTestid: 'report-action',
    title: 'Create an Advanced Report',
  },
  {
    name: BASE_ROUTES.DEAL_NAVIGATOR,
    icon: 'opportunity',
    dataTestid: 'deal-nav-action',
    title: 'Win faster with Deal Navigator',
  },
  {
    name: 'create_population',
    icon: 'population',
    dataTestid: 'population-action',
    title: 'Create a Custom Population',
  },
  {
    name: 'team',
    icon: 'team',
    dataTestid: 'team-action',
    title: 'Invite more teammates',
  },
];

const { iteratively } = useIteratively();
type Link = {
  name: string;
  icon: string;
  dataTestid: string;
  title: string;
};
function getProps(link: Link) {
  return {
    'data-testid': link.dataTestid,
    type: 'white',
    variant: 'outline',
    text: link.title,
    leftIcon: ['fak', link.icon],
    size: 'large',
  } as BittsButtonProps;
}
const hasTrackedUpgrade = ref(false);
function trackUpgrade(title?: string) {
  if (hasTrackedUpgrade.value) return;
  hasTrackedUpgrade.value = true;
  iteratively.userUpgradesToConnector({
    cta: title || '',
    event_site: EVENT_SITES.SELF_SERVE_FLOW,
  });
}
function getEventHandlers(link: Link) {
  return {
    click: () => {
      trackUpgrade(link.title);
      router.push({ name: link.name });
    },
  };
}

onMounted(() => {
  window.addEventListener('beforeunload', () => trackUpgrade());
});
onUnmounted(() => {
  window.removeEventListener('beforeunload', () => trackUpgrade());
});
onBeforeRouteLeave(() => {
  trackUpgrade();
});

async function handleClose(closeButton = false) {
  if (closeButton) {
    trackUpgrade('Close');
  } else {
    trackUpgrade(
      !isOnboarded.value ? 'Finish onboarding' : 'Continue to Crossbeam',
    );
  }
  if (!isOnboarded.value) {
    await router.push({ name: 'home' });
    return;
  }
  await router.push({ name: 'team' });
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.purchase-successful {
  @apply flex flex-1 flex-col items-center px-16 pb-48 min-h-screen;
  /* This comes directly from Figma 😁 */
  background: radial-gradient(
      70.48% 50% at 0% 100%,
      color-mix(in srgb, theme(colors.brand-teal) 80%, transparent),
      color-mix(in srgb, theme(colors.brand-teal) 0%, transparent)
    ),
    radial-gradient(
      50.07% 71.24% at 100% 0%,
      color-mix(in srgb, theme(colors.secondary-accent) 80%, transparent),
      color-mix(in srgb, theme(colors.secondary-accent) 0%, transparent)
    ),
    theme(colors.brand-navy);
}

.welcome-to-the-team__content {
  @apply flex flex-col items-center h-full flex-1 md:mt-80;
}

.welcome-to-the-team__logo {
  @apply w-64 h-64 md:w-80 md:h-80 mb-24 mt-72;
}

.main-title {
  background: linear-gradient(
    91.59deg,
    theme(colors.info-accent) 0%,
    theme(colors.white) 100%
  );
  @apply text-xl md:text-xxxl bg-clip-text font-bold mb-40 text-center;
  -webkit-text-fill-color: transparent;
  text-fill-color: transparent;
}

.description {
  @apply text-m md:text-lg text-white text-center mb-40 opacity-75 max-w-full font-bold;
  max-width: 560px;
}

.action-button {
  @apply text-white rounded-16 py-16 flex items-center gap-8 pl-16 md:w-[330px] text-m border border-transparent border-r-0;
  background: linear-gradient(
    100.88deg,
    color-mix(in srgb, theme(colors.white) 20%, transparent),
    color-mix(in srgb, theme(colors.white) 0%, transparent)
  );

  &:hover {
    @apply border-info-accent border-r;
  }
}

.button-grid {
  @apply grid md:grid-cols-2 gap-24 w-full;
}

.close-icon {
  @apply absolute top-16 right-16;
}
</style>
